/**
 * CompanySettings.jsx (iter225) — Tenant-wide company preferences.
 *
 * Today: timezone (drives TMS on-time %, deviation KPI, arrival auto-flag).
 * Future: extend the same `settings.{key}` shape for locale/currency.
 */
import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { Buildings, FloppyDisk, Clock } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { getCompanySettings, updateCompanySettings } from '../lib/api';

function localOffsetLabel(tz) {
  try {
    // e.g. "GMT+10" — extracted from Intl.DateTimeFormat
    const parts = new Intl.DateTimeFormat('en-AU', {
      timeZone: tz, timeZoneName: 'shortOffset',
    }).formatToParts(new Date());
    const off = parts.find((p) => p.type === 'timeZoneName')?.value;
    return off ? ` · ${off}` : '';
  } catch {
    return '';
  }
}

export default function CompanySettings() {
  const [settings, setSettings] = useState(null);
  const [common, setCommon] = useState([]);
  const [defaultTz, setDefaultTz] = useState('Australia/Sydney');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await getCompanySettings();
        setSettings(r.data?.settings || {});
        setCommon(r.data?.common_timezones || []);
        setDefaultTz(r.data?.default_timezone || 'Australia/Sydney');
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Could not load settings');
      }
    })();
  }, []);

  const now = useMemo(() => {
    if (!settings?.timezone) return '';
    try {
      return new Intl.DateTimeFormat('en-AU', {
        timeZone: settings.timezone, hour: '2-digit', minute: '2-digit',
        weekday: 'short', day: '2-digit', month: 'short',
      }).format(new Date());
    } catch {
      return '';
    }
  }, [settings?.timezone]);

  const save = async () => {
    if (!settings?.timezone) return;
    setBusy(true);
    try {
      const r = await updateCompanySettings({ timezone: settings.timezone });
      setSettings(r.data?.settings || settings);
      toast.success('Company settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  if (!settings) {
    return <Layout><div className="p-6 text-sm text-muted-foreground">Loading…</div></Layout>;
  }

  return (
    <Layout>
      <div className="max-w-3xl space-y-4 p-1" data-testid="company-settings-page">
        <div>
          <p className="overline">Preferences</p>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <Buildings size={24} weight="duotone" /> Company Settings
          </h1>
          <p className="text-sm text-muted-foreground">
            Tenant-wide preferences that apply across every user in this workspace.
          </p>
        </div>

        <Card className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Clock size={18} className="text-emerald-600" weight="duotone" />
            <div>
              <div className="text-sm font-semibold">Timezone</div>
              <div className="text-xs text-muted-foreground">
                Used to interpret delivery windows, on-time %, ETA deviation
                and the driver-delayed auto-flag. Default when unset:
                <code className="ml-1">{defaultTz}</code>.
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">IANA timezone</Label>
              <Select
                value={settings.timezone}
                onValueChange={(v) => setSettings((s) => ({ ...s, timezone: v }))}
              >
                <SelectTrigger data-testid="cs-tz-select">
                  <SelectValue placeholder="Pick a timezone" />
                </SelectTrigger>
                <SelectContent>
                  {common.map((tz) => (
                    <SelectItem key={tz} value={tz} data-testid={`cs-tz-opt-${tz}`}>
                      {tz}
                      <span className="text-muted-foreground">{localOffsetLabel(tz)}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Current time in this zone</Label>
              <div
                className="rounded-md border bg-muted px-3 py-2 text-sm font-mono tabular-nums"
                data-testid="cs-tz-preview"
              >
                {now || '—'}
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <Button
              onClick={save}
              disabled={busy || !settings.timezone}
              data-testid="cs-save-btn"
            >
              <FloppyDisk size={16} className="mr-1" />
              {busy ? 'Saving…' : 'Save settings'}
            </Button>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
